import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, XCircle, ExternalLink, Loader2, Download } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Alert, AlertDescription } from './ui/alert';
import { ErrorState } from '@/components/common/ErrorState';
import { useJobStatus, type BulkJobFailureRow } from '../hooks/useJobStatus';
import { JobStatus, isTerminalJobStatus } from '../lib/jobs/jobStatus';
import { classifiedJobReason, failedJobReason } from '../lib/jobs/jobOutcome';
import { downloadJobResult } from '../lib/jobs/downloadJobResult';
import { cn } from '../lib/utils';
import { InvoiceStockErrorKeys } from '../types/apiErrorKeys';
import { formatBytes } from '../services/mediaApi';

/**
 * Bir background job'in akibetini gosteren slide-in drawer (shadcn Sheet).
 * useJobStatus hook'u 2sn'de bir poll'lar; terminal statu gelince polling durur,
 * drawer toplam Total/Processed/Failed sayilarini ve `failures[]` listesini gosterir.
 *
 * Reason key'leri lokalize edilir: `job.bulk.failure.reason.{key}` patterni; bulunmayan
 * key icin fallback olarak ham reason string gosterilir (forward-compat).
 */
export interface JobResultDrawerProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  jobId: string | null;
  /**
   * Failure row'larinda 'Detaya git' linki gosterilecekse navigation handler. Hook
   * jobType bazinda hangi alanin (orderId / invoiceId) tasidigini secer; consumer
   * bu callback'i alip kendi rotasina yonlendirir.
   */
  onNavigateToFailure?: (row: BulkJobFailureRow) => void;
}

const JobResultDrawer: React.FC<JobResultDrawerProps> = ({
  open, onClose, tenantId, jobId, onNavigateToFailure,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useJobStatus(tenantId, jobId, { enabled: open });
  const [downloading, setDownloading] = useState(false);
  /**
   * `hasResult` says the job wrote an output, not that the store still holds it. Only the 410
   * from an actual download attempt distinguishes the two, so this is the earliest point at
   * which the drawer can stop offering a file that is not coming back.
   */
  const [resultGone, setResultGone] = useState(false);

  const status = data?.status ?? JobStatus.Pending;
  const terminal = isTerminalJobStatus(status);
  const success = status === JobStatus.Completed && (data?.failedItems ?? 0) === 0;

  // Sonuc dosyasi olan job'lar (export CSV/Excel) icin dogrudan indirme — /jobs/{id}/download
  // blob stream eder. Bildirim deep-link'ine bagimli olmadan drawer'dan indirilebilir.
  const handleDownload = async () => {
    if (!jobId || downloading) return;
    setDownloading(true);
    const outcome = await downloadJobResult({
      tenantId,
      jobId,
      fallbackFileName: () => data?.resultFileName ?? t('job.bulk.drawer.downloadFallbackName'),
    });
    if (!outcome.ok && outcome.fileGone) setResultGone(true);
    setDownloading(false);
  };

  const titleKey = !data
    ? 'job.bulk.drawer.titleLoading'
    : status === JobStatus.Failed
      ? 'job.bulk.drawer.titleFailed'
      : status === JobStatus.Cancelled
        ? 'job.bulk.drawer.titleCancelled'
        : terminal
          ? success
            ? 'job.bulk.drawer.titleSuccess'
            : 'job.bulk.drawer.titlePartial'
          : 'job.bulk.drawer.titleRunning';

  return (
    <Sheet open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-5 py-4 border-b border-border shrink-0">
          <SheetTitle className="flex items-center gap-2 text-base">
            {!terminal && data
              ? <Loader2 className="size-4 animate-spin text-primary" />
              : success
                ? <CheckCircle2 className="size-4 text-success" />
                : status === JobStatus.Failed
                  ? <XCircle className="size-4 text-destructive" />
                  : <AlertTriangle className="size-4 text-warning" />}
            {t(titleKey)}
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">
            {data?.jobType
              ? t(`job.bulk.type.${data.jobType}`, { defaultValue: data.jobType })
              : ''}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-0">
          {isLoading && !data && (
            <div className="space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          )}

          {isError && (
            <ErrorState
              error={error}
              fallbackKey="job.bulk.drawer.fetchError"
              onRetry={refetch}
            />
          )}

          {data && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <StatCard
                  label={t('job.bulk.drawer.statTotal')}
                  value={data.totalItems}
                  tone="default"
                />
                <StatCard
                  label={t('job.bulk.drawer.statProcessed')}
                  value={data.processedItems}
                  tone="success"
                />
                <StatCard
                  label={t('job.bulk.drawer.statFailed')}
                  value={data.failedItems}
                  tone={data.failedItems > 0 ? 'destructive' : 'default'}
                />
              </div>

              {!terminal && (
                <Alert>
                  <AlertDescription className="flex items-center gap-2 text-sm">
                    <Loader2 className="size-3.5 animate-spin" />
                    {t('job.bulk.drawer.runningHint')}
                  </AlertDescription>
                </Alert>
              )}

              {terminal && success && (
                <Alert variant="success">
                  <AlertDescription className="text-sm">
                    {t('job.bulk.drawer.allSuccess', { count: data.processedItems })}
                  </AlertDescription>
                </Alert>
              )}

              {terminal && status === JobStatus.Failed && (
                <Alert variant="destructive">
                  <AlertDescription className="text-sm whitespace-pre-line">
                    {/* BackgroundJobLog.ErrorMessage'in yalniz `key|json` (classified reason,
                        ornegin credential-invalid) ya da eski satirlardaki ciplak anahtar formati
                        kullaniciya gosterilir. Diger her sey (yakalanmamis .NET exception'in ham
                        ex.Message'i) internal detay sayilir, asla ekrana basilmaz: jenerik mesaja
                        duser. */}
                    {failedJobReason(data.errorMessage, t)}
                  </AlertDescription>
                </Alert>
              )}

              {/* AB#1951 thermos fix (M1): Cancelled previously rendered NO error alert at all,
                  so a memory-backpressure pause (job.error.bulkImportPausedMemoryPressure, a
                  classified Cancelled reason, see MarketplaceImportBackpressurePausedException)
                  was invisible here even though JobsTab's inline row already surfaces it. Only
                  renders when there IS a classified message; an operator-requested cancel (no
                  ErrorMessage at all) still shows nothing, which is correct: there is nothing to
                  explain. `warning` (not `destructive`): this is a routine, self-healing pause,
                  not a failure. */}
              {terminal && status === JobStatus.Cancelled && classifiedJobReason(data.errorMessage, t) && (
                <Alert variant="warning">
                  <AlertDescription className="text-sm whitespace-pre-line">
                    {classifiedJobReason(data.errorMessage, t)}
                  </AlertDescription>
                </Alert>
              )}

              {terminal && data.hasResult && (
                <div className="space-y-2">
                  <Button
                    onClick={handleDownload}
                    disabled={downloading || resultGone}
                    // The reason a disabled control is disabled has to reach assistive tech, not
                    // just sighted users reading the alert underneath it.
                    aria-describedby={resultGone ? 'job-result-gone' : undefined}
                    className="w-full"
                  >
                    {downloading ? (
                      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Download className="size-4" aria-hidden="true" />
                    )}
                    {downloading
                      ? t('job.bulk.drawer.downloading')
                      : t('job.bulk.drawer.download')}
                  </Button>
                  {data.resultSizeBytes != null && !resultGone && (
                    <p className="text-xs text-muted-foreground text-center">
                      {t('job.bulk.drawer.resultSize', { size: formatBytes(data.resultSizeBytes) })}
                    </p>
                  )}
                  {resultGone && (
                    <Alert variant="destructive" id="job-result-gone">
                      <AlertDescription className="text-sm">
                        {t('job.result.fileNoLongerAvailable')}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {data.resultJson?.failures && data.resultJson.failures.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    {t('job.bulk.drawer.failuresHeading', { count: data.failedItems })}
                  </h3>
                  <ul className="divide-y divide-border border border-border rounded-lg overflow-hidden">
                    {data.resultJson.failures.map((row, idx) => (
                      <li
                        key={`${row.orderId ?? row.invoiceId ?? idx}-${idx}`}
                        className="px-3 py-2.5 flex items-start justify-between gap-3 text-sm hover:bg-muted/30"
                      >
                        <div className="min-w-0 flex-1">
                          {row.orderNumber && (
                            <code className="text-xs font-bold text-primary bg-primary/7 px-1.5 py-0.5 rounded">
                              {row.orderNumber}
                            </code>
                          )}
                          {!row.orderNumber && row.invoiceId && (
                            <code className="text-xs font-mono text-muted-foreground">
                              {row.invoiceId.slice(0, 8)}
                            </code>
                          )}
                          <p className="text-xs text-destructive mt-1 wrap-break-word">
                            {renderReason(row, t)}
                          </p>
                        </div>
                        {onNavigateToFailure && (row.orderId || row.invoiceId) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 shrink-0"
                            onClick={() => onNavigateToFailure(row)}
                            aria-label={t('job.bulk.drawer.navigateToItem')}
                          >
                            <ExternalLink className="size-3.5" />
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const StatCard: React.FC<{ label: string; value: number; tone: 'default' | 'success' | 'destructive' }> = ({
  label, value, tone,
}) => (
  <div className={cn(
    'rounded-lg border p-3',
    tone === 'success' && 'border-success/30 bg-success/10',
    tone === 'destructive' && 'border-destructive/30 bg-destructive/5',
    tone === 'default' && 'border-border bg-muted/20',
  )}>
    <div className="text-[0.7rem] uppercase tracking-wider text-muted-foreground font-medium">
      {label}
    </div>
    <div className={cn(
      'text-2xl font-bold mt-0.5 tabular-nums',
      tone === 'success' && 'text-success',
      tone === 'destructive' && 'text-destructive',
    )}>
      {value}
    </div>
  </div>
);

/** Reasons whose structured `params` render through a key of their own instead of `job.bulk.failure.reason.*`. */
const PARAMS_MESSAGE_KEYS: Record<string, string> = {
  insufficient_stock: InvoiceStockErrorKeys.StockInsufficient,
};

/** Legacy `insufficient_stock:Stok yetersiz: ...` rows already repeat the bundle entry's own prefix. */
const LEGACY_DETAIL_PREFIXES: Record<string, RegExp> = {
  insufficient_stock: /^stok yetersiz:\s*/i,
};

/**
 * Backend reason formati:
 *   - "<key>" + `params`    -> yapisal satir; params anahtarin cevirisine interpolasyonla basilir
 *   - "<key>"               -> typed reason (ornegin "placeholder_product")
 *   - "<key>:<detail>"      -> typed reason + ek detay metin (params'tan onceki satirlar dahil)
 *   - "unexpected:<detail>" -> tanimsiz hata; detail kullanicinin gormesi icin var
 *
 * i18n key bulunmazsa defaultValue olarak detail (varsa) veya raw reason fallback'i.
 * Bu sayede yeni typed reason eklendiginde frontend bundle'i guncellenmemis olsa bile
 * kullanici hata mesajini okuyabiliyor (forward-compat).
 */
function renderReason(
  row: BulkJobFailureRow,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const { reason, params } = row;
  if (!reason) return t('job.bulk.failure.reason.unknown');
  if (params) {
    return t(PARAMS_MESSAGE_KEYS[reason] ?? `job.bulk.failure.reason.${reason}`, { ...params, defaultValue: reason });
  }
  const colonIdx = reason.indexOf(':');
  if (colonIdx > 0) {
    const key = reason.slice(0, colonIdx);
    const rawDetail = reason.slice(colonIdx + 1);
    const legacyPrefix = LEGACY_DETAIL_PREFIXES[key];
    const detail = legacyPrefix ? rawDetail.replace(legacyPrefix, '') : rawDetail;
    return t(`job.bulk.failure.reason.${key}`, { detail, defaultValue: detail });
  }
  return t(`job.bulk.failure.reason.${reason}`, { defaultValue: reason });
}

export default JobResultDrawer;
